// hooks/client/useCustomerBreakdown.js - Updated with quarterly support
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiService from '../../services/api';
import { ENDPOINTS } from '../../services/endpoints';

const customerBreakdownQueryDefaults = {
  staleTime: 1000 * 60 * 5,     // 5 minutes
  gcTime: 1000 * 60 * 30,       // 30 minutes cache
  retry: 2,
  retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
};

export const useCustomerBreakdown = (startDate, endDate, preset = null) => {
  // Determine if we should include quarterly breakdown
  const shouldIncludeQuarters = useMemo(() => {
    return preset === 'ytd' || preset === 'last-year';
  }, [preset]);

  const {
    data: customerBreakdown,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['customerBreakdown', startDate, endDate, shouldIncludeQuarters],
    queryFn: async () => {
      const startTime = performance.now();
      
      const response = await apiService.get(ENDPOINTS.TIME_CUSTOMER_FEE_SUMMARY, {
        period_type: 'custom', 
        start_date: startDate,
        end_date: endDate,
        include_quarters: shouldIncludeQuarters
      });
      
      // console.log(`Customer data loaded in ${(performance.now() - startTime).toFixed(0)}ms`);
      
      return response;
    },
    enabled: !!(startDate && endDate), // Only fetch when dates are available
    ...customerBreakdownQueryDefaults,
  });

  const customerDetailsData = useMemo(() => {
    if (!customerBreakdown) return null;
    return {
      customer_summaries: customerBreakdown.customer_summaries || [],
      summary_stats: customerBreakdown.summary_metrics || {},
      quarterly_breakdown: customerBreakdown.quarterly_breakdown || [],
      quarters: customerBreakdown.quarters || []
    };
  }, [customerBreakdown]);

  return {
    // Data
    customerDetailsData,
    customerSummaries: customerDetailsData?.customer_summaries || [],
    summaryStats: customerDetailsData?.summary_stats || {},
    quarterlyBreakdown: customerDetailsData?.quarterly_breakdown || [],
    quarters: customerDetailsData?.quarters || [],
    
    // Loading states
    loading: isLoading,
    refreshing: isFetching,
    
    // Error handling
    error: isError ? (error?.message || 'Failed to load customer data') : null,
    isError,
    
    // Status
    initialized: !!customerBreakdown,
    hasData: !!(startDate && endDate),
    hasQuarterlyData: shouldIncludeQuarters && (customerDetailsData?.quarterly_breakdown?.length > 0),
    
    // Actions
    refetch,
  };
};